import type { RefObject } from "react";

import type { AssetSearchControllerPresentationValue } from "./AssetSearch.pres";
import type { AssetSearchProps } from "./AssetSearch.types";
import { useAssetSearchQueries } from "./useAssetSearchQueries";
import { useAssetSearchQuotes } from "./useAssetSearchQuotes";
import { useAssetSearchUi } from "./useAssetSearchUi";

export interface AssetSearchControllerInput extends AssetSearchProps {
  onNavigate: (symbol: string) => void;
}

export interface AssetSearchControllerValue {
  containerRef: RefObject<HTMLDivElement>;
  presentation: AssetSearchControllerPresentationValue;
}

/**
 * Orchestration hook for Asset Search.
 *
 * Invokes UI, then the three searches, then quote enrichment — unconditionally
 * and in that order. The connector supplies `onNavigate` for fallback routing;
 * the hook itself does not import router helpers.
 */
export const useAssetSearchController = ({
  value,
  onChange,
  onSelect,
  onClear,
  onNavigate,
  placeholder = "Search by symbol...",
  autofocus,
  showFilters = true,
  id,
  size,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  required,
}: AssetSearchControllerInput): AssetSearchControllerValue => {
  const ui = useAssetSearchUi({
    value,
    onChange,
    onSelect,
    onClear,
    onNavigate,
  });

  const queries = useAssetSearchQueries({
    query: ui.query,
    activeTab: ui.activeTab,
    showFilters,
  });

  const quotes = useAssetSearchQuotes({
    assets: queries.activeAssets,
  });

  return {
    containerRef: ui.containerRef,
    presentation: {
      query: ui.query,
      onQueryChange: ui.onQueryChange,
      onFocus: ui.onFocus,
      onClose: ui.onClose,
      isOpen: ui.isOpen,
      assets: quotes.assets,
      tabs: queries.tabs,
      activeTab: ui.activeTab,
      onTabChange: ui.onTabChange,
      highlightedIndex: ui.highlightedIndex,
      listRef: ui.listRef,
      onKeyDown: (event) =>
        ui.onKeyDown(event, {
          assets: quotes.assets,
          isLoading: queries.currentQuery.isLoading,
        }),
      onScroll: (event) =>
        ui.onScroll(event, {
          hasMore: !!queries.currentQuery.hasNextPage,
          isLoadingMore: queries.currentQuery.isFetchingNextPage,
          onLoadMore: queries.currentQuery.fetchNextPage,
        }),
      isLoading: queries.currentQuery.isLoading,
      isLoadingMore: queries.currentQuery.isFetchingNextPage,
      hasMore: !!queries.currentQuery.hasNextPage,
      onSelect: ui.onSelectAsset,
      onClear,
      placeholder,
      autofocus,
      showFilters,
      dropdownRef: ui.dropdownRef,
      id,
      size,
      className,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": ariaInvalid,
      required,
    },
  };
};
